import * as moment from "moment";

import { Appointment } from "@teamprevent/common/calendar";
import { TaskModel, TaskTypes } from "@teamprevent/common/task/taskActions/models";
import { CaseType } from "@teamprevent/common/model";
import { AppointmentAndParent } from "@teamprevent/common/calendar/models";
import { ClinicLocationTypeName } from "@teamprevent/common/appointment/models";

import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { TaskType } from "../task/model";
import { Team } from "../teams/model";
import { AdminUserInRedux } from "../crud/adminUser";
import { ClinicState, ClinicDetails } from "../calendar/clinics/models";
import { Customer } from "../crud/customer";

import { Location } from "redux-little-router";

interface Link {
    id: string;
    name: string;
}

export enum ChooseAndBookStatus {
    None = "None",
    Requested = "Requested",
    RequestedEmployeeToBook = "RequestedEmployeeToBook",
    TimeoutExpired = "TimeoutExpired",
}

export interface BookAppointmentFiltersModel {
    teams: string[];
    products: CaseType[];
    customers: string[];
    taskTypes: string[];
}

export interface BookAppointmentTaskListModel {
    id: string;
    type: string;
    taskType: string;
    referralCreated: moment.Moment | null;
    dueDate: moment.Moment | null;
    employee: Link;
    employeePostcode: string;
    customer: Link;
    ohCaseId: string;
    locationType: ClinicLocationTypeName;
    immunisationTypeTasks: Link[];
    chooseAndBookStatus: ChooseAndBookStatus;
    notes: string;
    caseType: CaseType;
}

export interface BookIntoClinicPayload {
    bookTask: BookAppointmentTaskListModel;
    appointment: Pick<AppointmentAndParent, "start" | "end" | "description" | "parentAppointment" | "customer" | "employee">;
}

export interface BookAppointmentTaskListModelDto {
    appointmentTasks: BookAppointmentTaskListModel[];
    data: BookAppointmentTaskListModelData;
}

export interface BookAppointmentTaskListModelData {
    count: number;
}

export interface BookAppointmentsState {
    bookAppointmentTasks: BookAppointmentTaskListModel[];
    bookAppointmentTasksData: BookAppointmentTaskListModelData;
    bookAppointmentTaskTypes: TaskType[];
    bookAppointmentErrors?: BookAppointmentErrors;
    calendarFilters: CalendarFiltersState;
}

export interface CalendarFiltersState {
    showOnlyClinicsWithInsufficientClinicians?: boolean;
    clinicType?: string;
    clinicLocationType?: ClinicLocationTypeName;
    focusDate?: moment.Moment;
    selectedUserIds?: string[];
    selectedRoomIds?: string[];
}

export interface ApplicationState {
    bookAppointments: BookAppointmentsState;
    teams: Team[];
    adminUsers: AdminUserInRedux[];
    clinic: ClinicState;
    router: Location;
    loading: any;
    customers: Customer[];
}

export interface BookIntoClinicFailResult {
    success: false;
}

export interface BookIntoClinicSuccessResult {
    success: true;
    incorrectProfession: boolean;
    tasks: TaskModel[];
    ohCaseId: string;
    clinic: ClinicDetails;
}

export interface RebookAppointmentSuccessResult {
    tasks: TaskModel[];
    ohCaseId: string;
    cancelledAppointmentId: string;
}

export interface AddBookingNotesSuccessResult {
    ohCaseId: string;
    taskId: string;
    notes: string;
}

export interface BookClinicAppointmentModel {
    locationType?: ClinicLocationTypeName;
    description: string;
    start: moment.Moment;
    end: moment.Moment;
}

export interface AdHocCalendarAppointmentModel {
    description: string;
    start: moment.Moment;
    end: moment.Moment;
    extendTillDay?: moment.Moment;
    teamPreventUsers: { id: string; name: string }[];
    bookingTypeId?: string;
    locationType?: ClinicLocationTypeName;
}

export interface BookAdHocClinicAppointmentModel {
    clinicId: string;
    description: string;
    start: moment.Moment;
    end: moment.Moment;
    teamPreventUsers: { id: string; name: string }[];
    bookingTypeId?: string;
}

export interface UpdateTimeNoteAppointmentModel {
    description: string;
    clinicianIds: string[];
    appointmentStartTime: moment.Moment;
    appointmentEndTime: moment.Moment;
    bookingTypeId?: string;
}

export interface CreateTimeNoteAppointmentModel extends UpdateTimeNoteAppointmentModel {
    extendTillDay: moment.Moment;
}

export interface BookAdHocAppointmentSuccessResult {
    ohCaseId: string;
    tasks: TaskModel[];
    bookTask: BookAppointmentTaskListModel;
    appointment: ServerModelWithDateStrings<Appointment>;
}

export interface BookAdHocClinicAppointmentSuccessResult {
    appointment: ServerModelWithDateStrings<Appointment>;
}

export interface BookAppointmentErrors {
    appointmentClash?: boolean;
    userClashIds: string[];
}

export interface BookAdHocAppointmentSuccessfulResult {
    success: true;
    errors?: BookAppointmentErrors;
    incorrectProfession?: boolean;
    appointment: ServerModelWithDateStrings<Appointment>;
    updatedTasks: TaskModel[];
}

export interface BookAdHocAppointmentFailedResult {
    success: false;
    errors: BookAppointmentErrors;
    incorrectProfession: boolean;
    appointment?: null;
    updatedTasks?: null;
}

export type BookAdHocAppointmentResult = BookAdHocAppointmentSuccessfulResult | BookAdHocAppointmentFailedResult;

export interface RebookingReasonModel {
    rebookingReason: string;
    rebookingReasonComment: string;
}

export interface RebookingAllModel {
    rebookingReason: string;
    rebookingReasonComment: string;
    appointmentTasks: TaskModel[];
}

export const FaceToFaceTaskTypesBookableIntoMobileClinic: string[] = [
    TaskTypes.BookHealthSurveillanceConsultationTask,
    TaskTypes.BookRepeatMedicalConsultationTask
];
